import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  UploadItem,
  UploadConfig,
  UploadStatus,
  ProgressUpdate,
  CsvDisplayMode,
} from './models/upload-item.model';

@Injectable({ providedIn: 'root' })
export class UploadManagerService {
  private uploadsSubject = new BehaviorSubject<UploadItem[]>([]);

  /** Reactive list of all active/completed uploads — consumed by the widget */
  readonly uploads$: Observable<UploadItem[]> = this.uploadsSubject.asObservable();

  // ─────────────────────────────────────────────────────────────
  // Public API — called by caller modules
  // ─────────────────────────────────────────────────────────────

  /**
   * Register a new upload in the queue.
   * Returns a frontend upload ID that the caller must use for all
   * subsequent progress/status updates.
   *
   * @example
   * const uploadId = this.uploadManager.addUpload({ fileName: 'data.csv', fileType: 'csv' });
   */
  addUpload(config: UploadConfig): string {
    const id = crypto.randomUUID();
    const item: UploadItem = {
      id,
      fileName: config.fileName,
      fileType: config.fileType,
      status: 'uploading',
      progress: 0,
      processedRecords: 0,
      totalRecords: config.totalRecords,
      statusLabel: config.statusLabel,
      csvDisplayMode: config.csvDisplayMode ?? 'percentage',
      abortController: new AbortController(),
      addedAt: new Date(),
    };
    this.uploadsSubject.next([...this.uploadsSubject.value, item]);
    return id;
  }

  /**
   * Link the backend-assigned upload ID to the frontend entry.
   * Call this after the upload API responds with an uploadId.
   * The backendUploadId is required for abort API calls and WebSocket subscriptions.
   *
   * @example
   * this.api.uploadCSV(file).subscribe(res => {
   *   this.uploadManager.setBackendId(frontendId, res.uploadId);
   *   // future: this.uploadWebSocket.connect(res.uploadId);
   * });
   */
  setBackendId(frontendId: string, backendUploadId: string): void {
    this.patchUpload(frontendId, { backendUploadId });
  }

  /**
   * Universal progress update method.
   * Called by the same entry point whether the update source is:
   *   - HTTP response / HttpClient reportProgress events  (current)
   *   - WebSocket message handler                         (future)
   *   - SSE / polling                                     (future)
   *
   * @example — HTTP (current)
   * this.uploadManager.updateProgress(uploadId, {
   *   processedRecords: event.processed,
   *   totalRecords: event.total,
   *   status: 'processing'
   * });
   *
   * @example — WebSocket (future, from UploadWebSocketService)
   * ws.onmessage = ({ data }) => {
   *   const msg: ProgressUpdate = JSON.parse(data);
   *   this.uploadManager.updateProgress(uploadId, msg);
   * };
   */
  updateProgress(uploadId: string, update: ProgressUpdate): void {
    const patch: Partial<UploadItem> = {};
    if (update.status !== undefined)           patch.status           = update.status;
    if (update.progress !== undefined)         patch.progress         = update.progress;
    if (update.processedRecords !== undefined) patch.processedRecords = update.processedRecords;
    if (update.totalRecords !== undefined)     patch.totalRecords     = update.totalRecords;
    if (update.statusLabel !== undefined)      patch.statusLabel      = update.statusLabel;
    this.patchUpload(uploadId, patch);
  }

  /** Convenience shorthand when only the status needs updating */
  updateStatus(uploadId: string, status: UploadStatus): void {
    this.patchUpload(uploadId, { status });
  }

  /**
   * Abort the upload for the given frontend ID.
   * - Fires the AbortController signal (cancels fetch/HttpClient request)
   * - Updates status to 'aborted' in the queue
   * - Caller is responsible for calling the backend abort API if needed
   *
   * To get the backendUploadId for the abort API call, use getBackendId().
   */
  abortUpload(uploadId: string): void {
    const item = this.uploadsSubject.value.find(u => u.id === uploadId);
    if (!item) return;
    item.abortController.abort();
    this.patchUpload(uploadId, { status: 'aborted' });
  }

  /** Returns the backend upload ID — use when calling the abort API */
  getBackendId(frontendId: string): string | undefined {
    return this.uploadsSubject.value.find(u => u.id === frontendId)?.backendUploadId;
  }

  /** Returns the AbortController — pass its .signal to fetch/HttpClient */
  getAbortController(uploadId: string): AbortController | undefined {
    return this.uploadsSubject.value.find(u => u.id === uploadId)?.abortController;
  }

  /** Remove a completed/failed/aborted entry from the widget */
  removeUpload(uploadId: string): void {
    this.uploadsSubject.next(
      this.uploadsSubject.value.filter(u => u.id !== uploadId)
    );
  }

  /** Clear all entries (e.g. on user logout) */
  clearAll(): void {
    this.uploadsSubject.next([]);
  }

  // ─────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────

  private patchUpload(uploadId: string, patch: Partial<UploadItem>): void {
    this.uploadsSubject.next(
      this.uploadsSubject.value.map(u =>
        u.id === uploadId ? { ...u, ...patch } : u
      )
    );
  }
}
